import { TourContext } from '../types';

/** `{{ctx.habitat.zooName}}` — whitespace tolerated inside the braces. */
const TOKEN = /\{\{\s*ctx\.([\w.]+)\s*\}\}/g;

const readPath = (ctx: TourContext | undefined, path: string): unknown =>
  path.split('.').reduce<unknown>((value, key) => {
    if (value === null || typeof value !== 'object') return undefined;
    return (value as Record<string, unknown>)[key];
  }, ctx);

/**
 * Substitute `{{ctx.*}}` tokens against the tour context.
 *
 * Returns `undefined` when any token cannot be resolved, which callers treat exactly like a
 * `pending` condition: render nothing. Emitting the raw token, or an empty string, would put
 * "We are LIVE from ." on screen and then correct it once the data arrived — the same flicker
 * the tri-state conditions exist to prevent.
 */
export const interpolate = (template: string | undefined, ctx: TourContext | undefined): string | undefined => {
  if (!template) return template;

  let unresolved = false;
  const result = template.replace(TOKEN, (_match, path: string) => {
    const value = readPath(ctx, path);
    if (value === undefined || value === null) {
      unresolved = true;
      return '';
    }
    return String(value);
  });

  return unresolved ? undefined : result;
};

/** Does this string carry at least one token? Lets callers skip work on static copy. */
export const hasTokens = (template: string | undefined): boolean => {
  if (!template) return false;
  TOKEN.lastIndex = 0; // the regex is global and stateful; reset before a bare .test()
  return TOKEN.test(template);
};
